import { useMemo } from 'react';
import { isEqual } from 'lodash';

import useEvent from '@lyearn/core/hooks/useEvent';
import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { Popover, usePopoverContext } from '@lyearn/ui';

import SortMenuButton from './components/SortMenuButton';
import SortMenuItems from './components/SortMenuItems';
import { SortMenuItemsProps } from './components/SortMenuItems/types';
import { SortMenuItemType, SortMenuProps } from './types';
import { getEnabledSortItems } from './utils';

const SortMenuPopover = (props: SortMenuItemsProps<SortMenuItemType>) => {
  const { isMobile } = useScreen();
  const { onClose } = usePopoverContext();
  const { items, label, value, onAction: onActionProp } = props;

  const onAction = useEvent((item: SortMenuItemType) => {
    onClose();
    onActionProp(item);
  });

  return (
    <Popover.Content
      minWidth={17.6}
      position="bottom-start"
      renderAs={isMobile ? 'bottom-sheet' : 'popover'}>
      <SortMenuItems items={items} label={label} value={value} onAction={onAction} />
    </Popover.Content>
  );
};

const SortMenu = (props: SortMenuProps) => {
  const { t } = useTranslation();
  const {
    defaultPlaceholder = t('Common:Actions.Sort')!,
    defaultSortById,
    items: initialItems,
    sort,
    onSortChange,
  } = props;

  const items = useMemo(() => getEnabledSortItems(initialItems), [initialItems]);

  const selectedItems = useMemo(
    () => items.filter((item) => isEqual(item.sort, sort)),
    [items, sort],
  );

  const onAction = useEvent((item: SortMenuItemType) => {
    onSortChange(item.sort);
  });

  if (!items.length) {
    return null;
  }

  const value = selectedItems[0]?.id || defaultSortById || items[0].id;

  return (
    <Popover>
      <Popover.Button>
        <SortMenuButton defaultPlaceholder={defaultPlaceholder} selectedItems={selectedItems} />
      </Popover.Button>
      <SortMenuPopover items={items} label={defaultPlaceholder} value={value} onAction={onAction} />
    </Popover>
  );
};

export default SortMenu;
